/**
 * Puzzly Cap Service — Phase 7 / Slot Model.
 *
 * Centralizes the active-slot math for both the UX layer (predict whether
 * the user can publish another puzzle so we can suppress the publish button
 * AND surface an indicator like "2 of 3 puzzles") and the data-mutation
 * layer (activePuzzleCount increment/decrement in writeBatch via puzzleService).
 *
 * Exports:
 *   - getSlotState(profile)    — pure; returns { used, limit, atLimit }
 *   - canCreatePuzzle(profile) — pure boolean derived from getSlotState
 *   - useCapState(profile)     — React hook that re-renders hourly
 *
 * Design rules:
 *   - getSlotState is PURE. No Date.now() dependency.
 *   - Locked decision: client check is UX-only; Rules are the authoritative
 *     gate. canCreatePuzzle false → suppress button + open CapBlockerModal.
 *     canCreatePuzzle true but Rules deny → catch permission-denied and open
 *     CapBlockerModal anyway (two-layer defence).
 *   - Pro returns limit:30 (not unlimited). This enables "28 of 30 puzzles"
 *     rendering in CapUsageIndicator (RESEARCH Anti-Pattern warning).
 *
 * Logging: useCapState logs once per mount (debug), not per render — the hook
 * body is not a service-call "entry" event the way authService.js calls are.
 */

import { useState, useEffect } from "react";
import logger from "./logger";
import { activeLimit } from "../config/plans";

// ── Constants ─────────────────────────────────────────────────────────────

// Hourly ticker for useCapState — "X of Y puzzles" only needs to update
// approximately, and an hourly setInterval keeps the React tree quiet.
const HOOK_TICK_MS = 60 * 60 * 1000;

// ── Pure slot-state math ──────────────────────────────────────────────────

/**
 * Compute slot state from users/{uid} profile. Pure function.
 *
 * @param {object} profile — users/{uid} doc snapshot; tolerates undefined / {}.
 * @returns {{ used: number, limit: number, atLimit: boolean }}
 */
export function getSlotState(profile) {
  const limit = activeLimit(profile);
  const used = Number.isFinite(profile?.activePuzzleCount)
    ? profile.activePuzzleCount
    : 0;
  return { used, limit, atLimit: used >= limit };
}

/**
 * Predicate boolean for publish-handler gating.
 * @param {object} profile
 * @returns {boolean}
 */
export function canCreatePuzzle(profile) {
  return !getSlotState(profile).atLimit;
}

// ── React hook ────────────────────────────────────────────────────────────

/**
 * React hook that returns the live getSlotState(profile) and refreshes
 * approximately hourly. The profile itself updates via the App-level
 * onSnapshot subscription — this hook only adds the time-ticker.
 *
 * @param {object} profile
 * @returns {{ used: number, limit: number, atLimit: boolean }}
 */
export function useCapState(profile) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    // Once-per-mount debug trace (not per-render — avoids evicting the 500-entry
    // circular log buffer during a creator session). Demoted from info: a hook
    // body is not a service-call "entry" event.
    logger.debug("useCapState mount", { profile: !!profile });
    const id = setInterval(() => setTick((t) => t + 1), HOOK_TICK_MS);
    return () => clearInterval(id);
  }, []);
  // Re-evaluate on every render — profile change OR ticker. The `tick` is
  // referenced so React's exhaustive-deps lint is happy; it's not used in
  // the result itself.
  void tick;
  return getSlotState(profile);
}
